import { FC, useEffect, ReactNode, useState, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Sidebar, Button, Grid, PageTitle, useIntl, useToast } from "@metsooutotec/modes-react-components"
import AssetSectionEditorCloseModal from "../editor/asset-section-editor-close"
import asEditorStore from "./local-store/asset-section-editor-store"
import * as helper from "./helpers/svg-helpers"
import * as saveUtil from "../editor/editor-utils/save-utils"
import linersLocationsStores from "features/organizations/asset-sections/store/liner-locations-store"
import { ReScaleSetExistingRegionPath } from "./helpers/holes-helper"
import { commonOnSaveProps, SaveFunctions, CssSVGClasses, EditorSteps } from "./editor-utils/editor-types"
import SVGEditorWearReferencePlaneModal from "./modals/svg-editor-wrp-modal"
import { generateSteps } from "./sections/steps-generator"
import { SideBarContents } from "./sections/side-bar-contents"
import { FirstColumnEditor } from "./sections/first-column-editor"
import { SecondColumnEditor } from "./sections/second-column-editor"
import { observer } from "mobx-react-lite"
import { style } from "../editor/asset-section-editor-styles"
import customerStore from "features/organizations/customers/store/customer-store"
import { Asset } from "features/organizations/assets/models/asset-models"
import { EditingAssetSectionWindow } from "../../models/asset-section-models"
import { getAssetSectionSVG } from "../../services/asset-section-services"

const AssetSectionEditor: FC = observer(() => {
    //store
    const { assetSectionSettings, setAssetSectionSettings } = asEditorStore
    const { currentStep, backCurrentStep, nextCurrentStep, resetAll } = asEditorStore
    const { selectedLiners, setSelectedLiners } = asEditorStore
    const { selectedWearReferencePlane, setSelectedWearReferencePlane } = asEditorStore
    const { selectedholes, setSelectedHoles } = asEditorStore
    const { selectedLinersDrawingRef, setSelectedLinersDrawingRef } = asEditorStore
    const { assetSectionImage, setAssetSectionImage } = asEditorStore

    // #region Consts and States
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { setMessage } = useToast()
    const { customerId, siteId, assetId, assetSectionId } = useParams()
    const { assetMap, assetSectionMap } = customerStore
    const asset: Asset = assetMap[assetId || ""]
    const assetSection = assetSectionMap[assetSectionId || ""]
    const navBack = `/customers/${customerId}/sites/${siteId}/assets/${asset.id}/assetsections/${assetSectionId}`

    const [isCloseConfirmOpen, setCloseConfirmOpen] = useState(false)
    const [loadingAssetImage, setLoadingAssetImage] = useState(false)
    const [showRightSidebar, setShowRightSidebar] = useState<boolean>(true)
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [isModalWearReferencePlaneOpen, setIsModalGlobalWearReferencePlaneOpen] = useState<boolean>(false)

    // Used for collect the data from the UI then post them to the backend.
    const [newEditAssetSection, setNewEditAssetSection] = useState<EditingAssetSectionWindow>({
        id: assetSectionId as string,
        assetId: assetId as string,
        customerId: customerId as string,
        description: assetSection.description,
        radiusHint: 0.2,
        lineWeight: 1,
        name: assetSection.name,
        sensorNotificationArea: assetSection.sensorNotificationArea <= 0 ? 4 : assetSection.sensorNotificationArea,
        siteId: siteId as string,
        assetSectionSvg: null,
        assetSectionPdf: null,
    })

    // #endregion

    useEffect(() => {
        resetAll()
    }, [])

    // #region fetchs

    const fetchImage = async () => {
        setLoadingAssetImage(true)
        const imageUrl = await getAssetSectionSVG(assetSectionId as string)
        setAssetSectionImage(imageUrl)
        setLoadingAssetImage(false)

        // Search for the first Liner and take its stroke width.
        if (imageUrl) {
            await fetch(imageUrl as string)
                .then(response => response.text())
                .then(content => {
                    // Parse the fetched content to an SVG Document.
                    const parser = new DOMParser()
                    const svgDocument = parser.parseFromString(content, "image/svg+xml")

                    // Get the first assigned liner.
                    const firstLiner = svgDocument.querySelector(`path.${CssSVGClasses.WsLiner}`)
                    if (firstLiner) {
                        // Get the stroke-width value of the first path.
                        const strokeWidth = firstLiner.getAttribute("stroke-width")
                        if (strokeWidth) {
                            const strokeWidthNumber = parseFloat(strokeWidth)
                            setAssetSectionSettings({
                                ...assetSectionSettings,
                                lineWeight: strokeWidthNumber,
                                sensorNotificationArea: assetSection.sensorNotificationArea,
                            })
                        }
                    }
                })
                .catch(error => {
                    console.error("Error fetching Blob content:", error)
                })
        }
    }
    const memoizedFetchImage = useMemo(() => fetchImage, [assetSectionId])

    // #endregion

    // #region steps
    const steps = generateSteps(selectedLiners, selectedWearReferencePlane, selectedholes, selectedLinersDrawingRef)
    // #endregion

    // #region Svg editing handlers

    const svgEditingHandlers = {
        /** Get the data from the details and settings and set them into this assetSectionImage */
        onAssetSectionSettingsChange: async () => {
            const { svgDocument } = await helper.fetchSvgImageFromUrl(assetSectionImage as string)

            // Modify the paths in the SVG
            const liners: NodeListOf<SVGPathElement> = svgDocument.querySelectorAll(`path.${CssSVGClasses.WsLiner}`)
            const assignedHoles: NodeListOf<SVGPathElement> = svgDocument.querySelectorAll(`path.${CssSVGClasses.WsHole}`)
            liners?.forEach(liner => {
                liner.setAttribute("stroke", "black")
                liner.setAttribute("stroke-width", `${assetSectionSettings.lineWeight}`)
            })

            assignedHoles?.forEach(hole => {
                ReScaleSetExistingRegionPath(document, hole, assetSectionSettings.sensorNotificationArea)
            })
            const blobUrl = await helper.updateAssetSectionImage(svgDocument)
            setAssetSectionImage(blobUrl)

            newEditAssetSection.radiusHint = assetSectionSettings.radiusHint
            newEditAssetSection.sensorNotificationArea = assetSectionSettings.sensorNotificationArea
            newEditAssetSection.lineWeight = assetSectionSettings.lineWeight
            asEditorStore.setEditAssetSectionWindow(newEditAssetSection)
        },
    }
    // #endregion

    // #region useEffects

    useEffect(() => {
        linersLocationsStores.loadLinerLocations(assetSectionId as string)
    }, [currentStep === EditorSteps.WearReferencePlane])

    useEffect(() => {
        if (!newEditAssetSection.description) setErrorMessage("Description must not be empty")
        else if (!newEditAssetSection.name) setErrorMessage("Name must not be empty")
        else setErrorMessage("")
    }, [newEditAssetSection])

    useEffect(() => {
        svgEditingHandlers.onAssetSectionSettingsChange()
    }, [assetSectionSettings])

    useEffect(() => {
        memoizedFetchImage()
    }, [memoizedFetchImage])
    // #endregion

    // #region Save process

    const commonOnSaveProps: commonOnSaveProps = {
        assetSectionId: assetSectionId as string,
        assetSectionImage,
        errorMessage,
        setLoadingAssetImage,
        setMessage,
    }

    const onSave: SaveFunctions = {
        [EditorSteps.SVGSettings]: async () =>
            await saveUtil.onSaveSettingsAndDetails({ ...commonOnSaveProps, newEditAssetSection, fetchImage, setNewEditAssetSection }),
        [EditorSteps.LinersLocation]: async () => await saveUtil.onSaveLiners({ ...commonOnSaveProps, selectedLiners, setSelectedLiners }),
        [EditorSteps.WearReferencePlane]: async () =>
            await saveUtil.onSaveWearReferencePlane({ ...commonOnSaveProps, selectedWearReferencePlane, setSelectedWearReferencePlane }),
        [EditorSteps.HolesLocation]: async () => await saveUtil.onSaveHoles({ ...commonOnSaveProps, selectedholes, setSelectedHoles }),
        [EditorSteps.LinersDrawingReferences]: async () =>
            await saveUtil.onSaveLinerDrawingReferences({ ...commonOnSaveProps, selectedLinersDrawingRef, setSelectedLinersDrawingRef }),
    }

    const handleSaveClick = async () => {
        const saveFunction = onSave[currentStep]
        if (saveFunction) {
            await saveFunction()
        } else {
            console.warn(`No save function defined for this step: ${currentStep}`)
        }
    }

    // #endregion

    // #region Navigate Buttons

    const navigateButtonsHandlers = {
        previous: () => {
            if (currentStep > 0) {
                backCurrentStep()
                setErrorMessage("")
            }
        },
        next: () => {
            if (currentStep < steps.length - 1) {
                nextCurrentStep()
                setErrorMessage("")
            }
        },
        close: () => {
            setErrorMessage("")
            setCloseConfirmOpen(true)
        },
    }

    const navigateButtons: ReactNode = (
        <>
            <Button icon='icon-cross-circle-16' labelPosition='left' onClick={navigateButtonsHandlers.close} secondary>
                {formatMessage("assetSectionEditor.close")}
            </Button>
            <Button icon='icon-plus-16' labelPosition='left' onClick={handleSaveClick} secondary>
                {formatMessage("assetSectionEditor.save")}
            </Button>
            <Button
                icon='icon-arrow-left-16'
                labelPosition='left'
                disabled={currentStep === EditorSteps.SVGSettings}
                onClick={navigateButtonsHandlers.previous}
                secondary
            >
                {formatMessage("assetSectionEditor.previous")}
            </Button>
            <Button
                icon='icon-arrow-right-16'
                labelPosition='right'
                disabled={currentStep === steps.length - 1}
                onClick={navigateButtonsHandlers.next}
                primary
            >
                {formatMessage("assetSectionEditor.next")}
            </Button>
        </>
    )

    return (
        <>
            <style>{style}</style>
            <PageTitle onPathChange={navigate} title={formatMessage("assetSectionEditor.title")} extraItems={navigateButtons} />
            <div
                className='editor-container'
                style={{
                    position: currentStep !== EditorSteps.SVGSettings ? "absolute" : "inherit",
                    display: currentStep !== EditorSteps.SVGSettings ? "contents" : "table-cell",
                }}
            >
                <Sidebar.Pushable>
                    {currentStep !== EditorSteps.SVGSettings && (
                        <Sidebar
                            id={"rightSidebar"}
                            direction='right'
                            animation='push'
                            visibleWhenCollapsed
                            visible={showRightSidebar}
                            setVisible={setShowRightSidebar}
                            onClick={() => {
                                setShowRightSidebar(!showRightSidebar)
                            }}
                            header={
                                <Sidebar.Title
                                    title={steps[currentStep].sideBarTitle}
                                    onClick={() => {
                                        setShowRightSidebar(!showRightSidebar)
                                    }}
                                    visible={showRightSidebar}
                                />
                            }
                        >
                            <Sidebar.Content>
                                <SideBarContents />
                            </Sidebar.Content>
                        </Sidebar>
                    )}
                    <Sidebar.Pusher
                        content={
                            <div className='grid-container'>
                                <Grid columns={2} stretched stackable>
                                    <FirstColumnEditor
                                        asset={asset}
                                        assetSection={assetSection}
                                        errorMessage={errorMessage}
                                        newEditAssetSection={newEditAssetSection}
                                        setNewEditAssetSection={setNewEditAssetSection}
                                    />
                                    <SecondColumnEditor
                                        assetSectionId={assetSectionId}
                                        assetSectionSettings={assetSectionSettings}
                                        loadingAssetImage={loadingAssetImage}
                                        newEditAssetSection={newEditAssetSection}
                                        setIsModalGlobalWearReferencePlaneOpen={setIsModalGlobalWearReferencePlaneOpen}
                                    />
                                </Grid>
                            </div>
                        }
                    />
                </Sidebar.Pushable>
            </div>
            {isCloseConfirmOpen && <AssetSectionEditorCloseModal isOpen={isCloseConfirmOpen} setIsOpen={setCloseConfirmOpen} navigateTo={navBack} />}
            {isModalWearReferencePlaneOpen && (
                <SVGEditorWearReferencePlaneModal
                    assetSectionId={assetSectionId as string}
                    isModalOpen={isModalWearReferencePlaneOpen}
                    setIsModalOpen={setIsModalGlobalWearReferencePlaneOpen}
                />
            )}
        </>
    )
})

export default AssetSectionEditor
